import type { IAreaBoundary, PersistedDestinationDraft } from '@/entities/destination'
import type { DestinationFormValue } from '@/features/destination'
import type {
  CreateDestinationPayload,
  ILocalizedDestinationMap,
  UpdateDestinationPayload,
} from '@/shared/api/types/destination-types'
import { useNavigate, useParams } from '@tanstack/react-router'
import { ArrowLeft, Loader2, Save } from 'lucide-react'
import { startTransition, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCountriesQuery, useProvincesQuery } from '@/entities/country'
import {
  buildDestinationDraft,
  hasDestinationDraftContent,
  useCreateDestinationMutation,
  useDestinationByIdQuery,
  useUpdateDestinationMutation,
} from '@/entities/destination'
import { useLanguagesQuery } from '@/entities/tenant'
import {
  DestinationFormFields,
  DestinationMapPicker,
  DestinationTranslationSection,
  isDestinationType,
} from '@/features/destination'
import {
  useCreateTranslationDraft,
  useLocalizedTranslationJobController,
} from '@/features/localized-translation'
import { normalizeLocalizedDataMap } from '@/shared/api/mappers/localized-data-mapper'
import { AdminScopeType } from '@/shared/constants'
import { TRANSLATION_JOB_SCOPE } from '@/shared/constants/status'
import { useAdminScope } from '@/shared/hooks/use-admin-scope'
import { useUnsavedChangesGuard } from '@/shared/hooks/use-unsaved-changes-guard'
import { buildScopedAdminPath } from '@/shared/libs/admin-routes'
import { getApiErrorText } from '@/shared/libs/get-api-error-text'

import { showToast } from '@/shared/libs/toast'
import { Button } from '@/shared/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/shared/ui/dialog'
import { PageContainer } from '@/shared/ui/page-container'
import { PageHeader } from '@/shared/ui/page-header'
import { SectionHeader } from '@/shared/ui/section-header'
import { hasRequiredTranslations } from '@/shared/utils/language-utils'

const DEFAULT_FORM: DestinationFormValue = {
  countryId: '',
  provinceId: '',
  slug: '',
  destinationType: '',
  areaBoundary: null,
  centerPoint: null,
  banner: '',
  thumbnail: '',
  isActive: true,
}

type EditSection = 'translations' | 'details' | 'map'

/** Normalize API's areaBoundary (coordinates-only array or full GeoJSON object) to IAreaBoundary. */
function normalizeApiAreaBoundary(raw: unknown): IAreaBoundary | null {
  if (!raw) return null
  if (typeof raw === 'object' && !Array.isArray(raw) && 'type' in raw) {
    const obj = raw as Record<string, unknown>
    if (obj['type'] === 'Polygon' && Array.isArray(obj['coordinates'])) {
      return { type: 'Polygon', coordinates: obj['coordinates'] as number[][][] }
    }
  }
  if (Array.isArray(raw) && raw.length > 0) {
    return { type: 'Polygon', coordinates: raw as number[][][] }
  }
  return null
}

interface DestinationTranslationValue {
  language: string
  title: string
  name: string
  description: string
}

function buildFormSnapshot(value: DestinationFormValue): string {
  return JSON.stringify(value)
}

function buildTranslationSnapshot(translations: DestinationTranslationValue[]): string {
  return JSON.stringify(
    translations.map((item) => ({
      language: item.language,
      title: (item.title ?? '').trim(),
      name: (item.name ?? '').trim(),
      description: (item.description ?? '').trim(),
    })),
  )
}

function mapLocalizedDataToTranslations(
  localizedData: ILocalizedDestinationMap | null | undefined,
  languages: string[],
): DestinationTranslationValue[] {
  const normalizedLocalizedData = normalizeLocalizedDataMap(localizedData)
  return languages.map((language) => {
    const localized = normalizedLocalizedData?.[language]
    return {
      language,
      title: localized?.title ?? '',
      name: localized?.name ?? '',
      description: localized?.description ?? '',
    }
  })
}

function buildLocalizedDataPayload(
  translations: DestinationTranslationValue[],
): ILocalizedDestinationMap {
  return Object.fromEntries(
    translations.map((item) => [
      item.language,
      {
        title: (item.title ?? '').trim(),
        name: (item.name ?? '').trim(),
        description: (item.description ?? '').trim(),
      },
    ]),
  )
}

export function DestinationFormPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const params = useParams({ strict: false })
  const { isTenantAdmin, scopeType, tenantSlug } = useAdminScope()
  const destinationId = params.destinationId ?? ''
  const isEdit = Boolean(destinationId)
  const isReadonly = isTenantAdmin && isEdit
  const draftEntityId = useMemo(() => 'draft:destination:global', [])
  const translationEntityId = isEdit && destinationId ? destinationId : draftEntityId

  const [form, setForm] = useState<DestinationFormValue>(DEFAULT_FORM)
  const [translations, setTranslations] = useState<DestinationTranslationValue[]>([])
  const [initialSnapshot, setInitialSnapshot] = useState(() => buildFormSnapshot(DEFAULT_FORM))
  const [initialTranslationSnapshot, setInitialTranslationSnapshot] = useState(() =>
    buildTranslationSnapshot([]),
  )
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState<string | null>(null)
  const [isBannerUploading, setIsBannerUploading] = useState(false)
  const [isThumbnailUploading, setIsThumbnailUploading] = useState(false)
  const [, setHasPendingTranslationGeneration] = useState(false)
  const [savingSection, setSavingSection] = useState<EditSection | null>(null)
  const currentSnapshot = useMemo(() => buildFormSnapshot(form), [form])
  const currentTranslationSnapshot = useMemo(
    () => buildTranslationSnapshot(translations),
    [translations],
  )
  const isDetailsDirty = isEdit && currentSnapshot !== initialSnapshot
  const isTranslationDirty = isEdit && currentTranslationSnapshot !== initialTranslationSnapshot
  const hasUnsavedChanges =
    !isReadonly &&
    (isEdit
      ? isDetailsDirty || isTranslationDirty
      : currentSnapshot !== initialSnapshot ||
        currentTranslationSnapshot !== initialTranslationSnapshot)
  const { dialog: unsavedChangesDialog, allowNextNavigation } =
    useUnsavedChangesGuard(hasUnsavedChanges)
  const currentScope =
    scopeType === AdminScopeType.Tenant && tenantSlug
      ? { type: AdminScopeType.Tenant as const, tenantSlug }
      : { type: AdminScopeType.Super as const }
  const destinationsPath = buildScopedAdminPath(currentScope, '/destinations')

  const createMutation = useCreateDestinationMutation()
  const updateMutation = useUpdateDestinationMutation()
  const destinationQuery = useDestinationByIdQuery(destinationId)
  const languagesQuery = useLanguagesQuery(true)
  const { clearEntityJobs, runningJobs } = useLocalizedTranslationJobController({
    scope: TRANSLATION_JOB_SCOPE.DESTINATION,
    entityId: translationEntityId,
    audioEnabled: false,
  })
  const destinationDraft = useMemo(
    () =>
      buildDestinationDraft({
        form,
        translations,
      }),
    [form, translations],
  )
  const hasDestinationDraft = useMemo(
    () =>
      hasDestinationDraftContent(
        {
          form,
          translations,
        },
        DEFAULT_FORM,
      ),
    [form, translations],
  )
  const hydrateDestinationDraft = useCallback((persistedDraft?: PersistedDestinationDraft) => {
    const persistedForm = persistedDraft?.form
    const nextForm: DestinationFormValue = {
      countryId: persistedForm?.countryId ?? DEFAULT_FORM.countryId,
      provinceId: persistedForm?.provinceId ?? DEFAULT_FORM.provinceId,
      slug: persistedForm?.slug ?? DEFAULT_FORM.slug,
      destinationType: persistedForm?.destinationType ?? DEFAULT_FORM.destinationType,
      areaBoundary: persistedForm?.areaBoundary ?? DEFAULT_FORM.areaBoundary,
      centerPoint: persistedForm?.centerPoint ?? DEFAULT_FORM.centerPoint,
      banner: persistedForm?.banner ?? DEFAULT_FORM.banner,
      thumbnail: persistedForm?.thumbnail ?? DEFAULT_FORM.thumbnail,
      isActive: persistedForm?.isActive ?? DEFAULT_FORM.isActive,
    }
    const nextTranslations = (persistedDraft?.translations ?? []).map((row) => ({
      language: row.language,
      title: row.title ?? '',
      name: row.name ?? '',
      description: row.description ?? '',
    }))
    startTransition(() => {
      setForm(nextForm)
      setTranslations(nextTranslations)
      setInitialSnapshot(buildFormSnapshot(DEFAULT_FORM))
      setInitialTranslationSnapshot(buildTranslationSnapshot([]))
    })
  }, [])
  const { clearCurrentJobs } = useCreateTranslationDraft({
    enabled: !isEdit,
    scope: TRANSLATION_JOB_SCOPE.DESTINATION,
    entityId: translationEntityId,
    audioEnabled: false,
    initSignature: 'create:destination:global',
    draft: destinationDraft,
    hasContent: hasDestinationDraft,
    onHydrate: hydrateDestinationDraft,
  })
  const countriesQuery = useCountriesQuery({})
  const provincesQuery = useProvincesQuery({ countryId: form.countryId })
  const supportedLanguages = useMemo(
    () =>
      (languagesQuery.data?.data ?? [])
        .filter((item) => item.isActive && item.code?.trim())
        .map((item) => item.code.trim()),
    [languagesQuery.data?.data],
  )
  useEffect(() => {
    if (!destinationQuery.data?.data || !isEdit || supportedLanguages.length < 1) {
      return
    }
    const data = destinationQuery.data.data
    const nextValue: DestinationFormValue = {
      countryId: data.countryId,
      provinceId: data.provinceId,
      slug: data.slug,
      destinationType: data.destinationType ?? '',
      areaBoundary: normalizeApiAreaBoundary(data.areaBoundary),
      centerPoint:
        Array.isArray(data.centerPoint) && data.centerPoint.length >= 2
          ? ([Number(data.centerPoint[0]), Number(data.centerPoint[1])] as [number, number])
          : null,
      banner: data.banner ?? '',
      thumbnail: data.thumbnail ?? '',
      isActive: data.isActive,
    }
    const nextTranslations = mapLocalizedDataToTranslations(data.localizedData, supportedLanguages)
    startTransition(() => {
      setForm(nextValue)
      setTranslations(nextTranslations)
      setInitialSnapshot(buildFormSnapshot(nextValue))
      setInitialTranslationSnapshot(buildTranslationSnapshot(nextTranslations))
    })
  }, [destinationQuery.data?.data, isEdit, supportedLanguages])

  function buildNormalizedTranslations() {
    const baseTranslations =
      translations.length > 0
        ? translations
        : mapLocalizedDataToTranslations(null, supportedLanguages)

    return baseTranslations.map((item) => ({
      ...item,
      title: (item.title ?? '').trim(),
      name: (item.name ?? '').trim(),
      description: (item.description ?? '').trim(),
    }))
  }

  function buildDetailsPayload(): UpdateDestinationPayload | null {
    if (isBannerUploading || isThumbnailUploading) {
      showToast.error(
        t('tour.waitMediaUpload', { defaultValue: 'Please wait for image upload to finish.' }),
      )
      return null
    }

    if (
      !form.countryId ||
      !form.provinceId ||
      !form.slug.trim() ||
      !isDestinationType(form.destinationType)
    ) {
      showToast.error(
        t('common.requiredFieldsError', { defaultValue: 'Please fill required fields' }),
      )
      return null
    }
    if (!form.banner.trim() || !form.thumbnail.trim()) {
      showToast.error(
        t('tour.imagesRequired', { defaultValue: 'Please upload banner and thumbnail.' }),
      )
      return null
    }

    return {
      countryId: form.countryId,
      provinceId: form.provinceId,
      slug: form.slug.trim(),
      destinationType: form.destinationType.trim() || null,
      banner: form.banner.trim() || null,
      thumbnail: form.thumbnail.trim() || null,
      isActive: form.isActive,
    }
  }

  async function saveEditSection(
    section: EditSection,
    payload: UpdateDestinationPayload,
  ): Promise<boolean> {
    if (!destinationId || savingSection !== null) return false

    try {
      setSavingSection(section)
      await updateMutation.mutateAsync({ id: destinationId, payload })
      showToast.success(t('destination.updateSuccess'))
      return true
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
      return false
    } finally {
      setSavingSection(null)
    }
  }

  async function handleSaveTranslations() {
    const normalizedTranslations = buildNormalizedTranslations()
    if (!hasRequiredTranslations(normalizedTranslations, ['name', 'description'])) {
      showToast.error(t('destination.allLanguagesTranslationRequired'))
      return
    }

    const success = await saveEditSection('translations', {
      localizedData: buildLocalizedDataPayload(normalizedTranslations),
    })
    if (success) {
      clearEntityJobs(TRANSLATION_JOB_SCOPE.DESTINATION, translationEntityId)
      setInitialTranslationSnapshot(currentTranslationSnapshot)
      setHasPendingTranslationGeneration(false)
    }
  }

  async function handleSaveDetails() {
    const payload = buildDetailsPayload()
    if (!payload) {
      return
    }

    const success = await saveEditSection('details', payload)
    if (success) {
      setInitialSnapshot(currentSnapshot)
    }
  }

  async function handleSaveMapSection(
    boundary: IAreaBoundary | null,
    center: [number, number] | null,
  ) {
    if (!isEdit) return
    if (!boundary || !center) {
      showToast.error(
        t('destination.requiredAreaBoundary', {
          defaultValue: 'Please draw the area boundary and set the center point.',
        }),
      )
      return
    }

    const success = await saveEditSection('map', {
      areaBoundary: boundary.coordinates,
      centerPoint: center,
    })
    if (success) {
      setInitialSnapshot(
        buildFormSnapshot({ ...form, areaBoundary: boundary, centerPoint: center }),
      )
    }
  }

  async function handleSave() {
    if (isBannerUploading || isThumbnailUploading) {
      showToast.error(
        t('tour.waitMediaUpload', { defaultValue: 'Please wait for image upload to finish.' }),
      )
      return
    }

    if (
      !form.countryId ||
      !form.provinceId ||
      !form.slug.trim() ||
      !isDestinationType(form.destinationType)
    ) {
      showToast.error(
        t('common.requiredFieldsError', { defaultValue: 'Please fill required fields' }),
      )
      return
    }
    if (!form.banner.trim() || !form.thumbnail.trim()) {
      showToast.error(
        t('tour.imagesRequired', { defaultValue: 'Please upload banner and thumbnail.' }),
      )
      return
    }
    if (!form.areaBoundary || !form.centerPoint) {
      showToast.error(
        t('destination.requiredAreaBoundary', {
          defaultValue: 'Please draw the area boundary and set the center point.',
        }),
      )
      return
    }

    const baseTranslations =
      translations.length > 0
        ? translations
        : mapLocalizedDataToTranslations(null, supportedLanguages)
    const normalizedTranslations = baseTranslations.map((item) => ({
      ...item,
      title: (item.title ?? '').trim(),
      name: (item.name ?? '').trim(),
      description: (item.description ?? '').trim(),
    }))
    const shouldValidateTranslations =
      !isEdit || currentTranslationSnapshot !== initialTranslationSnapshot
    if (
      shouldValidateTranslations &&
      !hasRequiredTranslations(normalizedTranslations, ['name', 'description'])
    ) {
      showToast.error(t('destination.allLanguagesTranslationRequired'))
      return
    }
    const payload: CreateDestinationPayload = {
      countryId: form.countryId,
      provinceId: form.provinceId,
      slug: form.slug.trim(),
      destinationType: form.destinationType.trim() || null,
      areaBoundary: form.areaBoundary.coordinates,
      centerPoint: form.centerPoint,
      banner: form.banner.trim() || null,
      thumbnail: form.thumbnail.trim() || null,
      isActive: form.isActive,
      ...(shouldValidateTranslations
        ? { localizedData: buildLocalizedDataPayload(normalizedTranslations) }
        : {}),
    }

    try {
      if (isEdit && destinationId) {
        await updateMutation.mutateAsync({ id: destinationId, payload })
      } else {
        await createMutation.mutateAsync(payload)
      }
      clearCurrentJobs()
      allowNextNavigation()
      void navigate({ to: destinationsPath })
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    }
  }

  const isSaving =
    createMutation.isPending ||
    updateMutation.isPending ||
    isBannerUploading ||
    isThumbnailUploading ||
    languagesQuery.isLoading

  return (
    <PageContainer>
      <PageHeader
        title={
          isReadonly
            ? t('common.detail', { defaultValue: 'Detail' })
            : isEdit
              ? t('common.edit')
              : t('common.create')
        }
        breadcrumbs={[
          {
            label: t('sidebar.destinations', { defaultValue: 'Destinations' }),
            path: destinationsPath,
          },
          {
            label: isReadonly
              ? t('common.detail', { defaultValue: 'Detail' })
              : isEdit
                ? t('common.edit')
                : t('common.create'),
          },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => void navigate({ to: destinationsPath })}>
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              {t('common.back')}
            </Button>
            {!isReadonly && !isEdit && (
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-1.5 h-4 w-4" />
                )}
                {t('common.save')}
              </Button>
            )}
          </div>
        }
      />
      <div className="space-y-6">
        <div className="bg-white rounded-2xl border border-zinc-200 p-6 lg:p-7">
          <div className="mb-4 flex items-center justify-between gap-3">
            <SectionHeader title={t('tour.translations')} />
            {isEdit && !isReadonly && (
              <Button size="sm" onClick={handleSaveTranslations} disabled={savingSection !== null}>
                {savingSection === 'translations' && (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                )}
                {t('common.save')}
              </Button>
            )}
          </div>
          <div
            className={
              destinationQuery.isLoading || languagesQuery.isLoading
                ? 'opacity-60 pointer-events-none'
                : ''
            }
          >
            {runningJobs.length > 0 && (
              <p className="mb-3 text-sm text-blue-700">
                {t('jobs.runningCount', {
                  count: runningJobs.length,
                })}
              </p>
            )}
            <DestinationTranslationSection
              languages={supportedLanguages}
              translations={translations}
              onChange={setTranslations}
              onGenerationStateChange={setHasPendingTranslationGeneration}
              entityId={translationEntityId}
              entityLabel={
                translations.find((item) => item.name?.trim())?.name ??
                t('sidebar.destinations', { defaultValue: 'Destinations' })
              }
              readonly={isReadonly}
              {...(typeof window !== 'undefined' ? { entityPath: window.location.pathname } : {})}
            />
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-zinc-200 p-6 lg:p-7">
          <div className="mb-4 flex items-center justify-between gap-3">
            <SectionHeader title={t('destination.basicInfo', { defaultValue: 'Basic Info' })} />
            {isEdit && !isReadonly && (
              <Button size="sm" onClick={handleSaveDetails} disabled={savingSection !== null}>
                {savingSection === 'details' && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                {t('common.save')}
              </Button>
            )}
          </div>
          <div className={destinationQuery.isLoading ? 'opacity-60 pointer-events-none' : ''}>
            <DestinationFormFields
              value={form}
              countries={countriesQuery.data?.data ?? []}
              provinces={provincesQuery.data?.data ?? []}
              onChange={setForm}
              onPreview={setMediaPreviewUrl}
              onBannerUploadingChange={setIsBannerUploading}
              onThumbnailUploadingChange={setIsThumbnailUploading}
              onUploadSuccess={() =>
                showToast.success(
                  t('tour.imageUploadSuccess', { defaultValue: 'Image uploaded successfully' }),
                )
              }
              onUploadError={(message) =>
                showToast.error(
                  message || t('tour.imageUploadFailed', { defaultValue: 'Image upload failed' }),
                )
              }
              readonly={isReadonly}
            />
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-zinc-200 p-6 lg:p-7">
          <div className="mb-4 flex items-center justify-between gap-3">
            <SectionHeader
              title={t('destination.mapSection', { defaultValue: 'Boundary & Center Point' })}
            />
            {savingSection === 'map' && <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />}
          </div>
          <div className={destinationQuery.isLoading ? 'opacity-60 pointer-events-none' : ''}>
            <DestinationMapPicker
              areaBoundary={form.areaBoundary}
              centerPoint={form.centerPoint}
              onAreaBoundaryChange={(areaBoundary) =>
                setForm((prev) => ({ ...prev, areaBoundary }))
              }
              onCenterPointChange={(centerPoint) => setForm((prev) => ({ ...prev, centerPoint }))}
              {...(isEdit ? { onSaveMap: handleSaveMapSection } : {})}
              readonly={isReadonly}
              saving={savingSection === 'map'}
            />
          </div>
        </div>
      </div>
      <Dialog
        open={mediaPreviewUrl !== null}
        onOpenChange={(open) => {
          if (!open) {
            setMediaPreviewUrl(null)
          }
        }}
      >
        <DialogContent className="max-h-[90vh] max-w-[min(90vw,56rem)] gap-0 overflow-hidden p-2 sm:p-4">
          <DialogTitle className="sr-only">{t('common.preview')}</DialogTitle>
          {mediaPreviewUrl !== null && (
            <img
              src={mediaPreviewUrl}
              alt=""
              className="max-h-[min(85vh,calc(90vh-4rem))] w-full object-contain"
            />
          )}
        </DialogContent>
      </Dialog>
      {unsavedChangesDialog}
    </PageContainer>
  )
}
